<template>
	<div :class="['accordion', variant, !isExpandable && 'no-expandable']">
		<div
			v-if="isExpandable"
			:class="['accordion__trigger', open && 'is-open']"
			@click="handleClick"
			data-test="trigger"
		>
			<span
				v-if="variant !== 'accordion--vertical'"
				class="accordion__trigger-title"
				data-test="title"
			>
				{{ title }}
			</span>
			<span class="accordion__trigger-icon">
				<i
					:class="[
						'icon',
						open ? 'icon-arrow-up' : 'icon-arrow-down'
					]"
					v-if="variant !== 'accordion--vertical'"
				/>

				<i
					:class="[
						'icon',
						open ? 'icon-caret_up' : 'icon-caret_down'
					]"
					v-if="variant == 'accordion--vertical'"
				/>
			</span>
		</div>
		<div
			:class="['accordion__main', open && 'is-open']"
			v-if="variant == 'accordion--vertical'"
			data-test="main"
		>
			<slot name="main" />
		</div>
		<div
			v-show="open"
			class="accordion__body"
			ref="expandableContentRef"
			data-test="body"
		>
			<slot name="body-content" />
		</div>
	</div>
</template>

<script>
export default {
	name: 'Accordion',
	model: {
		prop: 'isOpen',
		event: 'click'
	},
	props: {
		// UI/UX NOTE: please set the right variant name and the styles
		variant: {
			type: String,
			default: '',
			validator: value =>
				['accordion--vertical', 'variant-2', ''].includes(value)
		},
		isExpandable: { type: Boolean, default: true },
		title: {
			type: String,
			default: ''
		},
		isOpen: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			open: this.isOpen
		};
	},
	mounted() {
		this.expandOrCollapse();
	},
	methods: {
		handleClick() {
			this.$emit('click', !this.isOpen);
			if (!this.isOpen) this.$emit('open');

			if (!this.isExpandable) {
				return;
			}

			this.open = !this.open;
			this.expandOrCollapse();

			this.$emit('click', this.open);
			this.$emit('expanded', this.open);
		},
		expandOrCollapse() {
			if (!this.isExpandable) {
				return;
			}

			if (this.open) {
				this.expand();
			} else {
				this.collapse();
			}
		},
		expand() {
			this.open = true;
		},
		collapse() {
			this.open = false;
		}
	},
	watch: {
		isOpen: {
			handler(newValue) {
				this.open = newValue;
				this.expandOrCollapse();
			}
		}
	}
};
</script>

<style lang="scss">
/* BASE */
.accordion {
	&:not(:last-child) {
		margin-bottom: $gutter-md;
	}

	&__trigger {
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: $padding-sm;
		background: $neutral-2;
		@include body-text('regular-emphasis');
		@include text-contrast($neutral-2, $white, $black);

		border-radius: 4px;

		&:focus-within,
		&:hover {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
			background: $white;
			border-radius: $form-border-radius;
			@include text-contrast($white, $white, $black);
		}
	}

	&__trigger-icon {
		display: flex;
		align-items: center;
		font-size: 18px;
	}

	&__body {
		border: 1px solid $neutral-2;
		border-radius: 0 0 4px 4px;
		border-top: none;
	}
}

.accordion__trigger.is-open {
	border-radius: 4px 4px 0 0;
}

/* VERTICAL */
.accordion--vertical {
	display: grid;
	grid-template-columns: 24px 1fr;

	.accordion__trigger {
		justify-content: center;
		border-radius: 4px 0 0 4px;
		padding: 0;
	}

	.accordion__trigger-icon {
		font-size: 14px;
	}

	.accordion__main {
		grid-column: 2 / 3;
		width: auto;
		border: 1px solid $neutral-3;
		border-left: 0;
		border-radius: 0 4px 4px 0;
		background-color: $white;
		overflow: hidden;
		z-index: 10;
	}

	.accordion__body {
		grid-column: 2 / 3;
		padding: 0px 36px;
		background-color: $neutral-4;
		border: none;
	}
}

.accordion__main.is-open {
	border-radius: 0 4px 0 0;
}

/* NO EXPANDABLE */
.no-expandable {
	.accordion__main {
		grid-column: 1 / 3;
		border: 1px solid $neutral-3;
		border-radius: 4px;
	}
}
</style>
